import { useCallback, useState } from 'react';

import isNil from 'lodash/isNil';

import type { Hashable } from '../string';
import { hashCode } from '../string';

type StorageType = 'local' | 'session';

type PersistentStorageOptions = Readonly<{
  storage: StorageType;
  hash?: Hashable;
}>;

const resolveStorage = (type: StorageType): Storage => (
  type === 'session' ? window.sessionStorage : window.localStorage
);

const toKey = (baseKey: string, hash: PersistentStorageOptions['hash']) => (
  `${baseKey}${isNil(hash) ? '' : hashCode(hash)}`
);

const writeToStorage = <T>(storage: Storage, key: string, value: T): void => {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // storage can be full or blocked (e.g. private mode)
  }
};

export const writePersistentStorage = <T>(
  baseKey: string,
  value: T,
  { storage, hash }: PersistentStorageOptions,
): void => (
  writeToStorage(resolveStorage(storage), toKey(baseKey, hash), value)
);

export const usePersistentStorage = <T>(
  baseKey: string,
  initialValue: T,
  { storage, hash }: PersistentStorageOptions,
): [T, React.Dispatch<React.SetStateAction<T>>, () => T] => {
  const storageRef = resolveStorage(storage);
  const key = toKey(baseKey, hash);

  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = storageRef.getItem(key);
      return !isNil(item) ? (JSON.parse(item) as T) : initialValue;
    } catch {
      return initialValue;
    }
  });

  // Stable, and functional updates see the latest value even when called twice in one render.
  const setValue = useCallback((value: T | ((val: T) => T)) => {
    setStoredValue((previous) => {
      const valueToStore = value instanceof Function ? value(previous) : value;
      writeToStorage(storageRef, key, valueToStore);
      return valueToStore;
    });
  }, [storageRef, key]);

  const reread = () => {
    const item = storageRef.getItem(key);
    const newValue = !isNil(item) ? (JSON.parse(item) as T) : storedValue;
    setStoredValue(newValue);

    return newValue;
  };

  return [storedValue, setValue, reread];
};

const useLocalStorage = <T>(
  baseKey: string,
  initialValue: T,
  hash?: Hashable,
): [T, React.Dispatch<React.SetStateAction<T>>, () => T] => (
  usePersistentStorage(baseKey, initialValue, { storage: 'local', hash })
);

export default useLocalStorage;
